import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useLanguage } from '../contexts/LanguageContext';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { User, X, Star, WhatsappLogo, Trash, Phone, Crown, FloppyDisk } from '@phosphor-icons/react';
import { useConfirmDialog } from './ConfirmDialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CustomersTab({ includeStaff = false } = {}) {
  const { t, language, fmtDate } = useLanguage();
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState('');
  const [detail, setDetail] = useState(null);
  const { ConfirmDialog, confirm } = useConfirmDialog();

  const fetchAll = async () => {
    try {
      // Iter31 FIX 5 — include_staff pulls admin/manager/staff rows too, sorted
      // staff-first, so AdminPage → "Përdorues Total" shows one unified list.
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (includeStaff) params.set('include_staff', 'true');
      const qs = params.toString();
      const r = await axios.get(`${API}/customers${qs ? `?${qs}` : ''}`,
                                { withCredentials: true });
      setRows(r.data || []);
    } catch (e) { toast.error('Failed'); }
  };

  useEffect(() => { fetchAll(); }, [search, includeStaff]);  // eslint-disable-line react-hooks/exhaustive-deps

  const openDetail = async (u) => {
    try {
      const r = await axios.get(`${API}/customers/${u.user_id}`, { withCredentials: true });
      setDetail(r.data);
    } catch (e) { toast.error('Failed'); }
  };

  const setRating = async (uid, rating) => {
    try {
      await axios.put(`${API}/customers/${uid}/review-rating`, { rating },
                      { withCredentials: true });
      toast.success('OK');
      // optimistically reflect in modal
      if (detail && detail.user.user_id === uid) {
        setDetail({ ...detail, user: { ...detail.user, review_rating: rating } });
      }
      fetchAll();
    } catch (e) { toast.error('Error'); }
  };

  // Iter30 — VIP card number set by manager/owner (numeric only, unique globally).
  const [vipDraft, setVipDraft] = useState('');
  const [vipSaving, setVipSaving] = useState(false);
  useEffect(() => {
    setVipDraft(detail?.user?.vip_card_number || '');
  }, [detail?.user?.user_id, detail?.user?.vip_card_number]);

  const saveVipCardNumber = async () => {
    if (!detail) return;
    setVipSaving(true);
    try {
      const r = await axios.put(
        `${API}/customers/${detail.user.user_id}/vip-card-number`,
        { vip_card_number: (vipDraft || '').trim() || null },
        { withCredentials: true },
      );
      const next = r.data?.vip_card_number || '';
      setDetail({ ...detail, user: { ...detail.user, vip_card_number: next } });
      setVipDraft(next);
      toast.success(t('vipCardNumberSaved') || 'Numri i kartës u ruajt');
      fetchAll();
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Gabim';
      toast.error(msg);
    } finally {
      setVipSaving(false);
    }
  };

  const deleteCustomer = async (u) => {
    const ok = await confirm({
      title: 'Je i sigurt?',
      description: `Ky veprim nuk kthehet.\n\nKlient: ${u.name || u.email}`,
      confirmText: 'Fshi',
      cancelText: 'Anulo',
      danger: true,
    });
    if (!ok) return;
    try {
      await axios.delete(`${API}/admin/users/${u.user_id}`, { withCredentials: true });
      toast.success('Klienti u fshi');
      setDetail(null);
      fetchAll();
    } catch (e) {
      const msg = e?.response?.data?.detail || '';
      if (e?.response?.status === 409) {
        toast.error('Klienti ka abonim aktiv. Anuloje së pari.');
      } else if (e?.response?.status === 403) {
        toast.error('Nuk ke leje për të fshirë klientë.');
      } else {
        toast.error(msg || 'Gabim gjatë fshirjes');
      }
    }
  };

  const locale = language === 'sq' ? 'sq-AL' : (language === 'it' ? 'it-IT' : 'en-US');

  const renderStars = (rating, onClick) => (
    <div className="flex items-center gap-0.5" data-testid="crm-stars">
      {[1, 2, 3, 4, 5].map((n) => (
        <button key={n} onClick={onClick ? () => onClick(n) : undefined}
                disabled={!onClick}
                data-testid={`crm-star-${n}`}
                className={`text-[#FFD700] ${onClick ? 'hover:scale-110' : 'cursor-default'} transition`}>
          <Star size={16} weight={n <= rating ? 'fill' : 'regular'} />
        </button>
      ))}
    </div>
  );

  return (
    <div className="space-y-3" data-testid="customers-tab">
      <Input value={search} onChange={(e) => setSearch(e.target.value)}
             placeholder={t('subscribersSearchPlaceholder')}
             className="h-10 bg-[#0a0a0a] border-white/10 rounded-xl text-sm"
             data-testid="crm-search" />

      <div className="space-y-2">
        {rows.length === 0 ? (
          <p className="text-center text-white/35 text-sm py-6">—</p>
        ) : rows.slice(0, 200).map((u) => {
          // Iter31 FIX 5 — role badge for non-customer rows (staff/mgr/admin).
          const isStaffRow = u.role && u.role !== 'user';
          const roleBadge = u.role === 'super_admin' ? { txt: 'Admin', cls: 'bg-[#FF5A00]/15 text-[#FF5A00]' }
                          : u.role === 'location_manager' ? { txt: 'Manager', cls: 'bg-[#0ABAB5]/15 text-[#0ABAB5]' }
                          : u.role === 'staff' ? { txt: 'Punonjës', cls: 'bg-[#FFD700]/15 text-[#FFD700]' }
                          : null;
          return (
          <button key={u.user_id} onClick={() => openDetail(u)}
                  data-testid={`crm-row-${u.user_id}`}
                  className={`w-full p-3 rounded-2xl border flex items-center justify-between gap-3 text-left transition ${
                    isStaffRow ? 'bg-white/[0.04] border-white/10 hover:border-[#FFD700]/30'
                               : 'bg-[#0a0a0a] border-white/5 hover:border-[#0ABAB5]/30'
                  }`}>
            <div className="flex items-center gap-2 min-w-0">
              <User size={18} weight="fill" className={isStaffRow ? 'text-[#FFD700]' : 'text-white/40'} />
              <div className="min-w-0">
                <p className="text-sm font-semibold text-white truncate flex items-center gap-1.5">
                  {u.name || u.email}
                  {roleBadge && (
                    <span className={`text-[9px] px-1.5 py-0.5 rounded uppercase tracking-wider font-bold ${roleBadge.cls}`}
                          data-testid={`crm-role-badge-${u.user_id}`}>
                      {roleBadge.txt}
                    </span>
                  )}
                </p>
                <p className="text-[11px] text-white/40 truncate">{u.phone || u.email}</p>
              </div>
            </div>
            <div className="text-right shrink-0">
              {/* Iter31 FIX 5 — customer-only metrics; hidden for staff/mgr/admin rows. */}
              {!isStaffRow && (
                <>
                  <p className="text-xs text-[#0ABAB5]">{(u.vip_total_spent || 0).toLocaleString(locale)} L</p>
                  <div className="flex items-center justify-end gap-1 mt-0.5">
                    {u.review_rating > 0 && (
                      <span className="text-[10px] text-[#FFD700] flex items-center gap-0.5">
                        <Star size={10} weight="fill" />{u.review_rating}
                      </span>
                    )}
                    <p className="text-[10px] text-white/40">{u.vip_free_washes || 0} ★</p>
                  </div>
                </>
              )}
              {isStaffRow && (
                <p className="text-[10px] text-white/40 uppercase tracking-wider">{u.email}</p>
              )}
            </div>
          </button>
          );
        })}
      </div>

      {/* Detail modal */}
      {detail && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-end sm:items-center justify-center p-4"
             onClick={() => setDetail(null)} data-testid="crm-detail-overlay">
          <div onClick={(e) => e.stopPropagation()}
               className="bg-[#0a0a0a] border border-white/10 rounded-2xl max-w-md w-full max-h-[85vh] overflow-y-auto"
               data-testid="crm-detail-modal">
            <div className="sticky top-0 bg-[#0a0a0a] border-b border-white/5 p-3 flex items-center justify-between z-10">
              <div className="min-w-0 flex-1">
                <p className="text-sm font-bold text-white truncate">{detail.user.name || detail.user.email}</p>
                <p className="text-[11px] text-white/45 truncate">
                  {detail.user.email}
                  {detail.user.phone && (
                    <a href={`https://wa.me/${detail.user.phone.replace(/[^0-9]/g, '')}`}
                       target="_blank" rel="noopener noreferrer"
                       className="ml-2 inline-flex items-center gap-1 text-[#25D366] hover:underline"
                       data-testid="crm-detail-whatsapp">
                      <WhatsappLogo size={11} weight="fill" />{detail.user.phone}
                    </a>
                  )}
                </p>
              </div>
              <button onClick={() => setDetail(null)} className="text-white/40 hover:text-white">
                <X size={20} />
              </button>
            </div>
            <div className="p-3 space-y-3">
              {/* Iter23 B2 — manual review rating (1-5 stars) */}
              <div className="p-3 rounded-xl bg-[#FFD700]/10 border border-[#FFD700]/20">
                <p className="text-[10px] text-white/55 uppercase tracking-wider mb-2">{t('crmReviewStatus') || 'Recensimi Google'}</p>
                {renderStars(detail.user.review_rating || 0, (n) => setRating(detail.user.user_id, n))}
                {(detail.user.review_rating || 0) > 0 && (
                  <button onClick={() => setRating(detail.user.user_id, 0)}
                          className="text-[10px] text-white/40 mt-1.5"
                          data-testid="crm-stars-clear">Hiq</button>
                )}
              </div>

              {/* Iter30 — VIP card number (manual, from Awoara terminal screen). */}
              <div className="p-3 rounded-xl bg-[#FFD700]/8 border border-[#FFD700]/20" data-testid="crm-vip-card-block">
                <p className="text-[10px] text-white/55 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                  <Crown size={11} weight="fill" className="text-[#FFD700]" />
                  {t('vipCardNumberLabel') || 'Numri i Kartës VIP'}
                </p>
                <div className="flex gap-2">
                  <Input
                    value={vipDraft}
                    onChange={(e) => setVipDraft(e.target.value.replace(/\D/g, ''))}
                    inputMode="numeric"
                    pattern="[0-9]*"
                    placeholder={t('vipCardNumberPlaceholder') || '0000 0000 0000'}
                    className="h-10 flex-1 bg-[#050505] border-white/10 rounded-lg text-sm tabular-nums"
                    data-testid="crm-vip-card-number-input"
                  />
                  <Button onClick={saveVipCardNumber} disabled={vipSaving || vipDraft === (detail.user.vip_card_number || '')}
                          className="h-10 px-3 rounded-lg bg-[#FFD700] hover:bg-[#FFD700]/90 text-black font-semibold text-xs"
                          data-testid="crm-vip-card-save">
                    <FloppyDisk size={14} weight="fill" className="mr-1" />
                    {vipSaving ? '…' : (t('save') || 'Ruaj')}
                  </Button>
                </div>
                <p className="text-[10px] text-white/40 mt-1.5 leading-snug">{t('vipCardNumberHelpManager') || 'Lexo numrin nga ekrani i terminalit Awoara.'}</p>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div className="p-2 rounded-xl bg-[#FF5A00]/10">
                  <p className="text-[10px] text-white/45 uppercase">{t('crmTotalSpent')}</p>
                  <p className="text-sm font-bold text-[#FF5A00]">{(detail.total_spent_lek || 0).toLocaleString(locale)} L</p>
                </div>
                <div className="p-2 rounded-xl bg-[#0ABAB5]/10">
                  <p className="text-[10px] text-white/45 uppercase">{t('crmWashCount')}</p>
                  <p className="text-sm font-bold text-[#0ABAB5]">{detail.washes?.length || 0}</p>
                </div>
                <div className="p-2 rounded-xl bg-white/5">
                  <p className="text-[10px] text-white/45 uppercase">VIP ★</p>
                  <p className="text-sm font-bold text-[#FFD700]">{detail.user.vip_free_washes || 0}</p>
                </div>
              </div>

              {detail.subscriptions?.length > 0 && (
                <div>
                  <p className="text-[11px] uppercase tracking-wider text-white/55 mb-1">{t('subscriptions') || 'Abonime'}</p>
                  {detail.subscriptions.slice(0, 5).map((s) => (
                    <div key={s.subscription_id || s.user_id + s.created_at}
                         className="text-xs text-white/70 p-2 mb-1 bg-[#050505] rounded-lg">
                      <p>{s.plan_name || s.plan_id} · <span className="text-white/50">{s.status}</span></p>
                      <p className="text-[10px] text-white/40">
                        {s.start_date ? fmtDate(s.start_date) : ''} → {s.end_date ? fmtDate(s.end_date) : ''}
                      </p>
                    </div>
                  ))}
                </div>
              )}
              {detail.bookings?.length > 0 && (
                <div>
                  <p className="text-[11px] uppercase tracking-wider text-white/55 mb-1">{t('bookingsTab')}</p>
                  {detail.bookings.slice(0, 5).map((b) => (
                    <div key={b.booking_id} className="text-xs text-white/70 p-2 mb-1 bg-[#050505] rounded-lg">
                      <p>{b.service_name_snapshot} · €{b.price_eur_snapshot}</p>
                      <p className="text-[10px] text-white/40">{b.scheduled_at?.slice(0, 16).replace('T', ' ')} · {b.status}</p>
                    </div>
                  ))}
                </div>
              )}
              {detail.washes?.length > 0 && (
                <div>
                  <p className="text-[11px] uppercase tracking-wider text-white/55 mb-1">{t('history') || 'Histori'}</p>
                  {detail.washes.slice(0, 5).map((w, i) => (
                    <div key={w.wash_id || w.transaction_id || `${w.completed_at}-${i}`} className="text-xs text-white/70 p-2 mb-1 bg-[#050505] rounded-lg flex justify-between">
                      <span>{w.program || w.program_name || '—'}</span>
                      <span className="text-[10px] text-white/40">{w.completed_at ? fmtDate(w.completed_at) : ''}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Iter23 B2 — Delete customer (super_admin only — backend enforces) */}
              <button onClick={() => deleteCustomer(detail.user)} data-testid="crm-delete-customer"
                      className="w-full h-10 rounded-xl bg-red-500/10 hover:bg-red-500/20 text-red-400 text-xs font-semibold flex items-center justify-center gap-2">
                <Trash size={13} weight="fill" /> Fshi klientin
              </button>
            </div>
          </div>
        </div>
      )}
      <ConfirmDialog />
    </div>
  );
}
